/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { rgba } from 'emotion-rgba';
import React, { Fragment, FunctionComponent, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import { useMeasure } from '@toucan/client/shared/hooks/use-measure';

import { styles } from './sticky-footer.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface StickyFooterProps {
    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

export type StyledStickyFooterProps = StickyFooterProps & {
    height: number;
};

const StyledStickyFooter = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

// custom CSS to override the cookie banner. This will push the cookie
// banner to sit above the sticky footer.
// we only pass a height when the sticky footer is being used, so this
// will set the bottom to 0px when it's not being used
const CookieBannerStylesOverride = createGlobalStyle<{ height: number }>`
  .optanon-alert-box-wrapper, #onetrust-banner-sdk {
    bottom: ${(props) => `${props.height}px`} !important;
    // set the z-index so that it displays behind intercom
    z-index: 1000 !important;
    box-shadow: 0 -0.3rem 1rem ${rgba('#0b002b', 0.1)}!important;
  }
`;

export const StickyFooter: FunctionComponent<StickyFooterProps> = ({
    ...otherProps
}: StickyFooterProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { height } = useMeasure(containerRef);

    return (
        <Fragment>
            <CookieBannerStylesOverride height={height} />
            <StyledStickyFooter height={height} {...otherProps}>
                <div
                    data-testid="sticky-footer-wrapper"
                    className="sticky-footer-wrapper"
                >
                    <div className="sticky-footer-gradient" />
                    <div
                        data-testid="sticky-footer-container"
                        id="sticky-footer"
                        className="sticky-footer-container"
                        ref={containerRef}
                    />
                </div>
                <div className="sticky-footer-shim" />
            </StyledStickyFooter>
        </Fragment>
    );
};
